import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  TrackByFunction,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { InteractionMode, PresentationSettings } from 'messages/websocket.messages';

import { InteractionModeDescription } from '../audio-settings/audio-settings.types';

@Component({
  selector: 'app-advanced-presentation-settings',
  templateUrl: './advanced-presentation-settings.component.html',
  styleUrls: ['./advanced-presentation-settings.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedPresentationSettingsComponent extends Loggable implements OnInit, OnDestroy {
  @Input() public allowGroupConversations = true;
  @Input() public defaultFullScreen = true;
  @Input() public requireFullScreen = false;
  @Input() public interactionMode: InteractionMode = InteractionMode.OPEN;
  @Output() public changeEvent = new EventEmitter<Partial<PresentationSettings>>();

  public menuOpen = true;
  protected groupConversationsV1 = false;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private interactionModesStatus: Map<InteractionMode, boolean> = new Map<InteractionMode, boolean>();

  public availableInteractionModes: readonly InteractionModeDescription[] = [
    {
      value: InteractionMode.SINGLE_PUSH_TO_SPEAK,
      label: 'Single Push to Speak',
      tooltip: 'One attendee at a time can push to react or speak to the entire room',
    },
    {
      value: InteractionMode.MANY_PUSH_TO_SPEAK,
      label: 'Many Push to Speak',
      tooltip: 'Multiple attendees at the same time can push to react or speak to the entire room',
    },
    {
      value: InteractionMode.QUEUED_Q_AND_A,
      label: 'Q & A',
      tooltip: 'Attendees push to ask questions for all attendees to hear; questions are queued',
    },
    {
      value: InteractionMode.NONE,
      label: 'No Audience Participation',
      tooltip: 'No one can react or speak to the presenter',
    },
    {
      value: InteractionMode.OPEN,
      label: 'Open Audience Participation',
      tooltip: 'Everyone can react or speak to the presenter',
    },
  ];

  public constructor(
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private store: Store<AppState>,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.setDisplayName('AdvancedPresentationSettingsComponent');
    this.matIconRegistry?.addSvgIcon(
      'help-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../../assets/images/help-icon.svg'),
    );

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.group_conversations_v1)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupConversationsV1) => {
        this.groupConversationsV1 = groupConversationsV1;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnInit(): void {
    this.availableInteractionModes.forEach((interactionMode) =>
      this.interactionModesStatus.set(interactionMode.value, true),
    );
  }

  protected trackModeByFn: TrackByFunction<InteractionModeDescription> = (index, item) => item.value;

  public handleDefaultFullScreenChange(): void {
    const defaultFullScreen = !this.defaultFullScreen;
    this.logUserAction('defaultFullScreenChange', 'click', { defaultFullScreen });
    this.changeEvent.emit({
      defaultFullScreen,
    });
  }

  public handleRequireFullScreenChange(): void {
    const requireFullScreen = !this.requireFullScreen;
    this.defaultFullScreen = true;
    this.logUserAction('requireFullScreenChange', 'click', { requireFullScreen });
    this.allowGroupConversations = !requireFullScreen;
    this.changeEvent.emit({ requireFullScreen });
    this.changeEvent.emit({ allowGroupConversations: this.allowGroupConversations });
    this.changeEvent.emit({ defaultFullScreen: this.defaultFullScreen });
    this.checkGroupConversations(this.allowGroupConversations);
  }

  public handleGroupConversationsChange(): void {
    const allowGroupConversations = !this.allowGroupConversations;
    this.logUserAction('groupConversationsChange', 'click', { allowGroupConversations });
    this.changeEvent.emit({ allowGroupConversations });
    this.checkGroupConversations(allowGroupConversations);
  }

  public handleInteractionModeChange({ value: interactionMode }: { value: InteractionMode }): void {
    this.logUserAction('interactionModeChange', 'click', { interactionMode });
    this.changeEvent.emit({
      interactionMode: interactionMode,
    });
  }

  public handleMenuClick(): void {
    this.logUserAction('menuToggle', 'click', { menuOpen: !this.menuOpen });
    this.menuOpen = !this.menuOpen;
  }

  public isInteractionModeDisabled(mode: InteractionMode): boolean {
    return !this.interactionModesStatus.get(mode);
  }

  private checkGroupConversations(allowGroupConversations: boolean): void {
    if (!this.groupConversationsV1) {
      if (!allowGroupConversations && this.interactionMode === InteractionMode.OPEN) {
        this.info('checkGroupConversations forcing many push to speak', {
          allowGroupConversations,
          interactionMode: this.interactionMode,
        });
        this.changeEvent.emit({ interactionMode: InteractionMode.MANY_PUSH_TO_SPEAK });
      }
      if (allowGroupConversations) {
        this.interactionModesStatus.set(InteractionMode.OPEN, true);
      } else {
        this.interactionModesStatus.set(InteractionMode.OPEN, false);
      }
    } else {
      if (!allowGroupConversations && this.interactionMode === InteractionMode.OPEN) {
        this.changeEvent.emit({ interactionMode: InteractionMode.OPEN });
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
